import { lazy } from "react";
import { createBrowserRouter } from "react-router-dom";
import { LazyPage } from "@/app/router/LazyPage";
import {
  GuestOnly,
  RequireAuth,
  RequireShellAccess,
  RootRedirect,
} from "@/app/router/guards";
import { NotFoundPage, ShellPlaceholder, UiGalleryPage } from "@/app/router/pages";
import {
  AuthShell,
  ExeShell,
  FieldShell,
  OpsShell,
  PrintShell,
} from "@/app/shells/Shells";
import { LoginPage } from "@/modules/auth";

import { sysRoutes } from "@/modules/sys/sysRoutes";
import { mdmRoutes } from "@/modules/mdm/mdmRoutes";
import { salesRoutes } from "@/modules/sales/salesRoutes";
import { financeRoutes } from "@/modules/finance/financeRoutes";
import { purchasingRoutes, purchasingFieldRoutes } from "@/modules/purchasing/purchasingRoutes";
import { planningRoutes } from "@/modules/planning/planningRoutes";
import { mesOpsRoutes, mesFieldRoutes } from "@/modules/mes/mesRoutes";
import { docsRoutes } from "@/modules/docs/docsRoutes";
import { engineeringRoutes } from "@/modules/engineering/engineeringRoutes";
import { biRoutes } from "@/modules/bi/biRoutes";
import { warehouseOpsRoutes, warehouseFieldRoutes } from "@/modules/warehouse/warehouseRoutes";
import { qmsRoutes, qmsFieldRoutes } from "@/modules/qms/qmsRoutes";
import { traceRoutes } from "@/modules/trace/traceRoutes";
import { fieldBaseRoutes } from "@/app/field/fieldRoutes";
import { exeRoutes } from "@/modules/exe/exeRoutes";
import { printRoutes } from "@/modules/print/printRoutes";
import { TestUiPage } from "@/modules/testui/TestUiPage";

const OpsOverviewDashboardPage = lazy(() =>
  import("@/modules/bi").then((m) => ({ default: m.OpsOverviewDashboardPage })),
);

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootRedirect />,
  },
  {
    path: "/testui",
    element: <TestUiPage />,
  },
  {
    path: "/auth",
    element: <GuestOnly />,
    children: [
      {
        element: <AuthShell />,
        children: [{ path: "login", element: <LoginPage /> }],
      },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <RequireShellAccess />,
        children: [
          {
            path: "/ops",
            element: <RequireShellAccess shell="ops" />,
            children: [
              {
                element: <OpsShell />,
                children: [
                  {
                    index: true,
                    element: (
                      <LazyPage>
                        <OpsOverviewDashboardPage />
                      </LazyPage>
                    ),
                  },
                  { path: "_ui", element: <UiGalleryPage /> },
                  { path: "testui", element: <TestUiPage /> },
                  ...sysRoutes,
                  ...mdmRoutes,
                  ...salesRoutes,
                  ...financeRoutes,
                  ...purchasingRoutes,
                  ...planningRoutes,
                  ...mesOpsRoutes,
                  ...docsRoutes,
                  ...engineeringRoutes,
                  ...biRoutes,
                  ...warehouseOpsRoutes,
                  ...qmsRoutes,
                  ...traceRoutes,
                ],
              },
            ],
          },
          {
            path: "/field",
            element: <RequireShellAccess shell="field" />,
            children: [
              {
                element: <FieldShell />,
                children: [
                  ...fieldBaseRoutes,
                  ...mesFieldRoutes,
                  ...warehouseFieldRoutes,
                  ...purchasingFieldRoutes,
                  ...qmsFieldRoutes,
                ],
              },
            ],
          },
          {
            path: "/exe",
            element: <RequireShellAccess shell="exe" />,
            children: [
              {
                element: <ExeShell />,
                children: [
                  ...exeRoutes,
                ],
              },
            ],
          },
          {
            path: "/print",
            element: <PrintShell />,
            children: [
              ...printRoutes,
            ],
          },
        ],
      },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);
